// AudioProvider — mounts the engine once, keeps player settings in sync, resolves the
// founder audible-preview entitlement from the backend, binds the authorized local manifest,
// drives route → audio-state transitions, and handles offline fade + reconnect restoration.
// No AudioContext is ever created until an explicit "Start Audio Preview" gesture.
import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { useConnectivity } from "./ConnectivityContext";
import audioManager from "../audio/AudioManager";
import { MusicStateMachine } from "../audio/MusicStateMachine";
import beatClock from "../audio/BeatClock";
import beatVisualController from "../audio/BeatVisualController";
import { useReducedMotion } from "../hooks/useReducedMotion";
import { fetchSettings, saveSettings, loadLocal, AUDIO_DEFAULTS } from "../audio/AudioSettings";
import { FALLBACK_CATALOG, stateForRoute } from "../audio/audioManifest";
import gameplayAudioAdapter from "../audio/GameplayAudioAdapter";
import { GameplayEvent } from "../audio/gameplayAudioEvents";
import api, { track } from "../api";

const Ctx = createContext(null);
export const useAudio = () => useContext(Ctx);

const stateMachine = new MusicStateMachine(FALLBACK_CATALOG);

export function AudioProvider({ children }) {
  const { user } = useAuth();
  const connectivity = useConnectivity();
  const status = connectivity?.status || "online";
  const location = useLocation();

  const [flags, setFlags] = useState(audioManager.flags);
  const [settings, setSettings] = useState(() => loadLocal() || { ...AUDIO_DEFAULTS });
  const [preview, setPreview] = useState(null);
  const [diag, setDiag] = useState(() => audioManager.getDiagnostics());
  const [gp, setGp] = useState(() => gameplayAudioAdapter.snapshot());
  const [bv, setBv] = useState(() => beatVisualController.snapshot());
  const reducedMotion = useReducedMotion();
  const loadedFor = useRef(null);
  const lastState = useRef({ state: "landing", exp: null });
  const settingsRef = useRef(settings);
  useEffect(() => { settingsRef.current = settings; }, [settings]);

  useEffect(() => { audioManager.onDiag(setDiag); return () => audioManager.onDiag(null); }, []);
  useEffect(() => gameplayAudioAdapter.subscribe(setGp), []);
  useEffect(() => { beatVisualController.onDiag(setBv); return () => beatVisualController.onDiag(null); }, []);
  useEffect(() => { beatVisualController.setReducedMotion(reducedMotion); }, [reducedMotion]);

  // Beat-sync activation gate — ALL conditions must hold. Ordinary players/admins can never
  // satisfy this because previewAuthorized is backend-issued. Reduced Motion / opt-out disable it.
  useEffect(() => {
    const d = audioManager.getDiagnostics();
    const gate = (
      d.previewAuthorized === true && d.previewStarted === true && d.emergency !== true &&
      d.canActivate === true && !!settings.beat_sync_enabled && !reducedMotion &&
      !!d.currentBpm
    );
    if (gate) {
      beatClock.configure({ bpm: d.currentBpm || 72 });
      const started = beatClock.start();
      if (!started && beatClock.running) beatClock.resync();
      beatVisualController.setActive(true);
    } else {
      beatVisualController.setActive(false);
      beatClock.stop();
    }
  }, [settings.beat_sync_enabled, reducedMotion, diag]);

  // Public catalog (flags + catalog). Offline → keep fallback catalog.
  useEffect(() => {
    let m = true;
    api.get("/audio/config").then((r) => {
      if (!m || !r.data) return;
      setFlags({ ...audioManager.configure(r.data.flags) });
      if (r.data.states) stateMachine.setCatalog(r.data.states);
    }).catch(() => {});
    return () => { m = false; };
  }, []);

  const refreshPreview = useCallback(async () => {
    if (!user) { audioManager.setPreviewAuth(false, false); audioManager.setManifest([]); setPreview(null); return null; }
    try {
      const st = (await api.get("/audio/preview/status")).data;
      setPreview(st);
      audioManager.setPreviewAuth(st.authorized, st.emergency_silence);
      if (st.authorized) {
        const man = (await api.get("/audio/manifest")).data;
        audioManager.setManifest(man.tracks || []);
        try {
          const cm = (await api.get("/audio/crew-motifs")).data;
          audioManager.setCrewManifest(cm.authorized ? (cm.tracks || []) : []);
        } catch (e) { audioManager.setCrewManifest([]); }
      } else {
        audioManager.setManifest([]);
        audioManager.setCrewManifest([]);
      }
      return st;
    } catch (e) { return null; }
  }, [user]);

  // Player settings + preview status when authenticated.
  useEffect(() => {
    let m = true;
    if (!user) { audioManager.setPreviewAuth(false, false); return; }
    if (loadedFor.current === user.id) return;
    loadedFor.current = user.id;
    fetchSettings().then(({ settings: s, flags: f }) => {
      if (!m) return;
      if (s) { setSettings(s); audioManager.applySettings(s); }
      if (f) setFlags({ ...audioManager.configure(f) });
    });
    refreshPreview();
    return () => { m = false; };
  }, [user, refreshPreview]);

  // First interaction (autoplay-safe). Still no AudioContext until startPreview().
  useEffect(() => {
    const h = () => audioManager.markInteraction();
    window.addEventListener("pointerdown", h, { once: true });
    window.addEventListener("keydown", h, { once: true });
    return () => { window.removeEventListener("pointerdown", h); window.removeEventListener("keydown", h); };
  }, []);

  // Offline fade + reconnect restore.
  useEffect(() => {
    audioManager.setOnline(status === "online");
    if (status !== "online") {
      if (audioManager.previewStarted) { audioManager.fadeOffline(); track("offline_audio_fade"); track("audio_context_suspended"); }
    } else if (audioManager.awaitingRestore) {
      (async () => {
        await refreshPreview(); // revalidate entitlement before making sound again
        if (audioManager.canActivate()) {
          await gameplayAudioAdapter.restore(lastState.current.state, lastState.current.exp);
          track("reconnect_audio_restore"); track("audio_context_resumed");
        }
      })();
    }
  }, [status, refreshPreview]);

  // PWA background/foreground.
  useEffect(() => {
    const h = () => { if (document.hidden) audioManager.suspend(); else audioManager.resume(); };
    document.addEventListener("visibilitychange", h);
    return () => document.removeEventListener("visibilitychange", h);
  }, []);

  // Route → audio-state (fallback; server-confirmed gameplay takes priority in the adapter).
  useEffect(() => {
    const st = stateForRoute(location.pathname);
    lastState.current = { state: st, exp: null };
    stateMachine.request(st);
    gameplayAudioAdapter.handle({ type: GameplayEvent.ROUTE_ENTERED, route: location.pathname, state: st });
  }, [location.pathname]);

  const updateSetting = useCallback(async (patch) => {
    const base = settingsRef.current;              // always current — no stale closure
    const next = { ...base, ...patch };
    settingsRef.current = next;
    setSettings(next);
    audioManager.applySettings(next);
    if (patch.beat_sync_enabled !== undefined) track(patch.beat_sync_enabled ? "beat_sync_visual_enabled" : "beat_sync_visual_disabled");
    if (audioManager.canActivate()) audioManager.playState(lastState.current.state, lastState.current.exp, { skipOneShots: true });
    const { settings: saved } = await saveSettings(base, patch);
    if (saved) { settingsRef.current = saved; setSettings(saved); audioManager.applySettings(saved); }
    return saved;
  }, []);

  // ---- founder preview controls ----
  const enablePreview = useCallback(async () => {
    await api.post("/audio/preview/enable");
    track("audio_preview_authorized");
    return refreshPreview();
  }, [refreshPreview]);

  const disablePreview = useCallback(async () => {
    audioManager.stopPreview(); track("audio_preview_stopped");
    await api.post("/audio/preview/disable");
    return refreshPreview();
  }, [refreshPreview]);

  const startPreview = useCallback(async () => {
    const r = await audioManager.startPreview();
    if (r.ok) {
      const next = { ...settingsRef.current, music_enabled: true, mute_all: false };
      settingsRef.current = next; setSettings(next);
      saveSettings(settingsRef.current, { music_enabled: true, mute_all: false });
      track("audio_preview_started");
      await audioManager.playState(lastState.current.state, lastState.current.exp);
    } else if (r.reason === "no_context") track("autoplay_blocked");
    return r;
  }, []);

  const stopPreview = useCallback(() => { audioManager.stopPreview(); track("audio_preview_stopped"); }, []);

  const setEmergency = useCallback(async (silence, reason) => {
    await api.post("/audio/preview/emergency", { silence: !!silence, reason: reason || "founder toggle" });
    return refreshPreview();
  }, [refreshPreview]);

  const audition = useCallback(async (state, expedition) => {
    lastState.current = { state, exp: expedition || null };
    return audioManager.playState(state, expedition || null);
  }, []);

  const auditionCrew = useCallback((crewId, role, opts) => audioManager.playCrewMotif(crewId, role || "base", opts || {}), []);
  const stopCrew = useCallback(() => audioManager.stopAllCrew(), []);
  const setPreviewVolume = useCallback((v) => audioManager.setPreviewVolume(v), []);
  const clearCache = useCallback(() => audioManager.clearCache(), []);
  const fadeOut = useCallback(() => audioManager.stopAll(false), []);
  const pause = useCallback(() => audioManager.suspend(), []);
  const resume = useCallback(() => audioManager.resume(), []);
  const beat = {
    trigger: (type) => beatVisualController.triggerManual(type),
    simulateReducedMotion: (v) => beatVisualController.setReducedMotion(v),
    pauseClock: () => beatClock.pause(),
    resumeClock: () => beatClock.resume(),
    resync: () => beatClock.resync(),
    clearLog: () => beatVisualController.clearLog(),
  };
  const gameplay = useCallback((evt) => {
    if (evt && (evt.type === GameplayEvent.SESSION_STARTED || evt.type === GameplayEvent.EXPEDITION_SELECTED)) {
      lastState.current = { state: "expedition_exploration", exp: evt.expedition || null };
    }
    return gameplayAudioAdapter.handle(evt);
  }, []);
  const resetSettings = useCallback(async () => {
    const s = audioManager.resetSettings();
    settingsRef.current = s; setSettings(s);
    await saveSettings(settingsRef.current, s);
    return refreshPreview();
  }, [refreshPreview]);

  // Dev-only diagnostic.
  useEffect(() => {
    if (process.env.NODE_ENV === "production") return;
    window.__ffAudio = {
      diagnostics: () => audioManager.getDiagnostics(),
      simulate: (s, exp) => audioManager.transitionTo(s, exp),
      gameplay: (evt) => gameplayAudioAdapter.handle(evt),
      adapter: gameplayAudioAdapter,
      manager: audioManager, stateMachine, beatClock,
      beatVisuals: beatVisualController,
      beatDiag: () => beatVisualController.snapshot(),
    };
    return () => { try { delete window.__ffAudio; } catch (e) {} };
  }, []);

  return (
    <Ctx.Provider value={{
      flags, settings, preview, diag, gp, bv, beat, reducedMotion, updateSetting, refreshPreview,
      enablePreview, disablePreview, startPreview, stopPreview, setEmergency,
      audition, setPreviewVolume, clearCache, fadeOut, resetSettings, pause, resume, gameplay,
      auditionCrew, stopCrew,
      diagnostics: () => audioManager.getDiagnostics(),
    }}>
      {children}
    </Ctx.Provider>
  );
}
